import logo from "../../assets/BiorLabs-white.svg";
import { Link } from "react-router-dom";
import {
  APP_STORE_DOWNLOAD_PATH,
  getAppStoreLinkProps,
  GOOGLE_PLAY_DOWNLOAD_PATH,
} from "../../utils/appLinks";
import { useApkRelease } from "../../utils/useApkRelease";
import { TOKEN_MARKET_LINKS } from "../../utils/tokenLinks";
import { useIntegrateModal } from "../../context/IntegrateModalContext";

const COL_LINKS = [
  {
    heading: "About",
    links: [
      { label: "Brand Kit",        nav: "/mediakit" },
      { label: "Whitepaper",       nav: "/whitepaper" },
      { label: "Terms of Service", nav: "/tos" },
      { label: "Privacy Policy",   nav: "/tos/privacy" },
    ],
  },
  {
    heading: "Products",
    links: [
      { label: "Cards",                    nav: "/cards" },
      { label: "BiorVault on Google Play", href: GOOGLE_PLAY_DOWNLOAD_PATH },
      { label: "BiorVault on App Store",   href: APP_STORE_DOWNLOAD_PATH, isAppStore: true },
      { label: "BiorVault APK",            isApk: true },
    ],
  },
  {
    heading: "Contact us",
    links: [
      { label: "Apply for Listing",   form: "token" },
      { label: "Get Integrated",      form: "dapp" },
      { label: "Collab / Partnership", form: "collab" },
    ],
  },
  {
    heading: "Token",
    links: TOKEN_MARKET_LINKS.map(({ label, href }) => ({ label, href })),
  },
  {
    heading: "Support",
    links: [
      { label: "Frequently Asked Questions", nav: "/faq" },
      { label: "Discord",          href: "https://discord.com/invite/hBs477K7AG" },
      { label: "Telegram",         href: "https://t.me/BiorLabsPortal" },
    ],
  },
];

export const Footer = () => {
  const appStoreLinkProps = getAppStoreLinkProps();
  const { url: apkUrl, version: apkVersion } = useApkRelease();
  const { openForm } = useIntegrateModal();

  return (
    <footer className="w-full border-t border-white/10 mt-4">
      {/* Main content */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-12 pb-10 sm:pt-16 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 lg:gap-20">

          {/* Brand column */}
          <div className="flex flex-col gap-5">
            <img src={logo} alt="BiorLabs" className="w-[8.125rem]" />
            <p className="text-white/65 text-sm font-dm leading-relaxed max-w-[16.25rem]">
              Bringing Web2 simplicity to Web3. Non-custodial, decentralized, and built for everyone.
            </p>
            <p className="text-white/40 text-xs font-dm leading-relaxed max-w-[16.25rem]">
              Not a licensed financial institution. All services are non-custodial.
            </p>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-8 lg:grid-cols-5">
            {COL_LINKS.map((col) => (
              <div key={col.heading} className="flex flex-col gap-3">
                <span className="text-xs font-dm font-semibold text-white/50 uppercase tracking-widest">
                  {col.heading}
                </span>
                {col.links.map((link) => {
                  const cls = "text-sm font-dm text-white/70 hover:text-white transition-colors duration-150 w-fit";
                  if (link.tooltip) {
                    return (
                      <span
                        key={link.label}
                        className="flex items-center gap-1.5 text-sm font-dm text-white/45 w-fit select-none"
                      >
                        {link.label}
                        <span className="text-[10px] font-medium tracking-wide text-white/28 bg-white/5 border border-white/10 rounded px-1 py-0.5">
                          Soon
                        </span>
                      </span>
                    );
                  }
                  if (link.isApk) {
                    return (
                      <a
                        key={link.label}
                        href={apkUrl ?? "#"}
                        download={apkUrl ? `BiorVault-v${apkVersion}.apk` : undefined}
                        className={cls}
                      >
                        {link.label}{apkVersion ? ` (v${apkVersion})` : ""}
                      </a>
                    );
                  }
                  if (link.nav) {
                    return (
                      <Link key={link.label} to={link.nav} className={cls}>
                        {link.label}
                      </Link>
                    );
                  }
                  if (link.form) {
                    return (
                      <button
                        key={link.label}
                        type="button"
                        onClick={() => openForm({ type: link.form })}
                        className={`${cls} text-left bg-transparent border-0 p-0 cursor-pointer font-dm`}
                        data-testid={`footer-open-form-${link.form}`}
                      >
                        {link.label}
                      </button>
                    );
                  }
                  return (
                    <a
                      key={link.label}
                      href={link.isAppStore ? appStoreLinkProps.href : link.href}
                      target={
                        link.isAppStore
                          ? appStoreLinkProps.target
                          : link.href?.startsWith("mailto")
                            ? undefined
                            : "_blank"
                      }
                      rel={link.isAppStore ? appStoreLinkProps.rel : "noreferrer"}
                      className={cls}
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 sm:py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-white/45 text-xs font-dm">
            BiorBk Inc. · RUC: 155765348-2-2025 · Registered in Panama
          </span>
          <div className="flex items-center gap-6">
            <a href="https://discord.gg/hBs477K7AG" target="_blank" rel="noreferrer" className="text-white/50 hover:text-white text-xs font-dm transition-colors duration-150">Discord</a>
            <a href="https://x.com/biorlabs" target="_blank" rel="noreferrer" className="text-white/50 hover:text-white text-xs font-dm transition-colors duration-150">X</a>
            <a href="https://t.me/BiorLabsPortal" target="_blank" rel="noreferrer" className="text-white/50 hover:text-white text-xs font-dm transition-colors duration-150">Telegram</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
